import React, { useState, useEffect, useMemo } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { getActiveCampaignRequiredFields } from '../../services/firestoreService';
import { db, firestorePathId } from '../../services/firebaseConfig';
import { onSnapshot, collection, query, where } from 'firebase/firestore'; 
import { 
    LayoutDashboard, 
    FileText, 
    CreditCard, 
    UserCheck, 
    ClipboardList, 
    Mail, 
    Key, 
    HelpCircle, 
    LogOut, 
    Sun, 
    Moon, 
    X as CloseIcon, 
    Repeat,
    User
} from 'lucide-react';

// --- Subcomponente: Título de Seção (Padrão RH) ---
const MenuSectionTitle = ({ title }) => (
    <div className="px-5 mt-6 mb-2 flex items-center">
        <h3 className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider flex-1">
            {title}
        </h3>
        <div className="h-px bg-gray-100 dark:bg-gray-800 w-full ml-2 flex-1"></div>
    </div>
);

// --- Subcomponente: Badge de Status do Recadastramento ---
const RecadastramentoStatusIndicator = ({ status, loading }) => {
    if (loading) return <span className="w-2 h-2 bg-gray-400 rounded-full animate-pulse ml-auto"></span>;

    const config = {
        pendente_revisao: { color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300', text: 'Análise' },
        aceito: { color: 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300', text: 'OK' },
        rejeitado: { color: 'bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300', text: 'Corrigir' },
        solicitacao_edicao: { color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-200', text: 'Editando' },
        default: { color: 'bg-orange-100 text-orange-800 dark:bg-orange-900/50 dark:text-orange-200', text: 'Pendente' }
    };

    const style = config[status] || config.default;

    return (
        <span className={`text-[10px] font-bold ml-auto px-2 py-0.5 rounded-full uppercase tracking-wider ${style.color}`}>
            {style.text}
        </span>
    );
};

// --- Componente Principal: Sidebar ---
const Sidebar = ({ isOpen, toggleSidebar, closeSidebar }) => {
    const { 
        user, 
        logout, 
        vinculos, 
        vinculoSelecionado, 
        limparVinculoSelecionado 
    } = useAuth();
    
    const { theme, toggleTheme } = useTheme();
    const navigate = useNavigate();

    // Estados de Regra de Negócio
    const [recadastramentoStatus, setRecadastramentoStatus] = useState(null);
    const [isRecadastramentoElegivel, setIsRecadastramentoElegivel] = useState(false);
    const [isLoadingStatus, setIsLoadingStatus] = useState(true);
    const [imgError, setImgError] = useState(false);

    // --- EFEITO: Monitorar Status Recadastramento ---
    useEffect(() => {
        if (!user || !vinculoSelecionado) {
            setIsLoadingStatus(false);
            setIsRecadastramentoElegivel(false);
            return;
        }

        let unsubscribe = () => {};
        setIsLoadingStatus(true);

        const setupListener = async () => {
            try {
                const campaign = await getActiveCampaignRequiredFields(user.municipioId);

                if (!campaign) {
                    setIsRecadastramentoElegivel(false);
                    setIsLoadingStatus(false);
                    return;
                }

                const filters = campaign.filters || {};
                const verificarElegibilidade = (vinc, campFilters) => {
                    let elegivel = true;
                    if (campFilters.admissaoTipos && campFilters.admissaoTipos.length > 0) {
                        if (!campFilters.admissaoTipos.includes(String(vinc.admissao_tipo))) elegivel = false;
                    }
                    if (elegivel && campFilters.situacaoFolha) {
                        if (vinc.situacao_folha !== campFilters.situacaoFolha) elegivel = false;
                    }
                    return elegivel;
                };

                const elegivel = verificarElegibilidade(vinculoSelecionado, filters);
                setIsRecadastramentoElegivel(elegivel);

                if (!elegivel) {
                    setIsLoadingStatus(false);
                    return;
                }

                const recadastramentoCollectionPath = `artifacts/${firestorePathId}/public/data/municipalities/${user.municipioId}/recadastramentos/${campaign.id}/servidores`;
                const q = query(collection(db, recadastramentoCollectionPath), where("userCpf", "==", user.cpf));

                unsubscribe = onSnapshot(q, (querySnapshot) => {
                    if (!querySnapshot.empty) {
                        const docSnap = querySnapshot.docs[0];
                        setRecadastramentoStatus(docSnap.data().status);
                    } else {
                        setRecadastramentoStatus(null);
                    }
                    setIsLoadingStatus(false);
                }, (error) => {
                    console.error("Erro listener sidebar:", error);
                    setIsLoadingStatus(false);
                });

            } catch (error) {
                console.error("Erro ao buscar campanha:", error);
                setIsLoadingStatus(false);
            }
        };

        setupListener();
        return () => unsubscribe();
    }, [user, vinculoSelecionado]);

    // --- FIX IOS: Bloquear Scroll do Body ---
    useEffect(() => {
        if (isOpen && window.innerWidth < 1024) {
            document.body.style.overflow = 'hidden';
            document.body.style.position = 'fixed';
            document.body.style.width = '100%';
        } else {
            document.body.style.overflow = '';
            document.body.style.position = '';
            document.body.style.width = '';
        }
        return () => {
            document.body.style.overflow = '';
            document.body.style.position = '';
            document.body.style.width = '';
        };
    }, [isOpen]);

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    const logoUrl = user?.logoUrl || 'https://firebasestorage.googleapis.com/v0/b/portal-sisfol.firebasestorage.app/o/Logos_municipais%2Fdefault.png?alt=media';
    
    // 🔹 ESTRUTURA DO MENU (Memoizada) 🔹
    const menuStructure = useMemo(() => {
        const permissions = user?.serverPortalPermissions || {};
        
        // Lógica de rota para contracheque
        const contrachequePath = vinculoSelecionado ? `/contracheques/${vinculoSelecionado.matricula}` : '/contracheques';

        const allItems = [
            { id: 'dashboard', label: 'Início', icon: LayoutDashboard, path: '/dashboard', category: 'Principal', permissionId: 'dashboard' },
            
            { id: 'ficha', label: 'Ficha Funcional', icon: FileText, path: '/ficha-funcional', category: 'Serviços', permissionId: 'fichaFuncional' },
            { id: 'contracheque', label: 'Contracheques', icon: CreditCard, path: contrachequePath, category: 'Serviços', permissionId: 'contracheque' },
            
            { 
                id: 'recadastramento', 
                label: 'Recadastramento', 
                icon: UserCheck, 
                path: '/recadastramento', 
                category: 'Campanhas', 
                permissionId: 'recadastramento',
                elegivel: isRecadastramentoElegivel,
                isRecadastramento: true
            },
            
            { id: 'requerimentos', label: 'Requerimentos', icon: ClipboardList, path: '/requerimentos', category: 'Solicitações', permissionId: 'requerimentos' },
            
            { id: 'avisos', label: 'Avisos', icon: Mail, path: '/avisos', category: 'Comunicação', permissionId: 'avisos' },
            
            { id: 'senha', label: 'Alterar Senha', icon: Key, path: '/change-password', category: 'Configurações', permissionId: 'changePassword' },
        // 🔹 CORREÇÃO DE ROTA E PERMISSÃO PARA O SUPORTE
            { id: 'suporte', label: 'Central de Suporte', icon: HelpCircle, path: '/ajuda', category: 'Comunicação', permissionId: 'suporte' },        ];

        // Filtragem de Permissões
        const permittedItems = allItems.filter(item => {
            if (item.permissionId === 'dashboard' || item.permissionId === 'changePassword') return true;
            const status = permissions[item.permissionId] || 'hidden';
            
            if (item.id === 'recadastramento') {
                return status === 'visible' && item.elegivel;
            }
            return status === 'visible';
        });

        // Agrupamento
        const grouped = permittedItems.reduce((acc, item) => {
            const cat = item.category;
            if (!acc[cat]) acc[cat] = [];
            acc[cat].push(item);
            return acc;
        }, {});

        const order = ['Principal', 'Serviços', 'Campanhas', 'Solicitações', 'Comunicação', 'Configurações'];
        return order.map(cat => ({
            category: cat,
            items: grouped[cat] || []
        })).filter(g => g.items.length > 0);

    }, [user, vinculoSelecionado, isRecadastramentoElegivel]);

    // --- Componente de Item de Menu ---
    const MenuItem = ({ item }) => {
        const baseClass = "flex items-center w-full text-left px-3 py-2.5 mx-2 rounded-lg transition-all duration-200 group relative";
        const inactiveClass = "text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800/50 hover:text-gray-900 dark:hover:text-gray-200";
        const activeClass = "bg-blue-50/80 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 font-medium shadow-sm ring-1 ring-blue-500/10";

        const shouldGoToReview = item.isRecadastramento && ['pendente_revisao', 'aceito', 'solicitacao_edicao'].includes(recadastramentoStatus);
        
        return (
            <NavLink
                to={item.path}
                state={shouldGoToReview ? { goToReview: true } : undefined}
                end={item.path === '/dashboard'}
                onClick={() => {
                    if (window.innerWidth < 1024 && closeSidebar) closeSidebar();
                }}
                className={({ isActive }) => `${baseClass} w-[calc(100%-16px)] ${isActive ? activeClass : inactiveClass}`}
            >
                <item.icon 
                    className={`w-[18px] h-[18px] mr-3 flex-shrink-0 transition-colors duration-200`} 
                    strokeWidth={2} 
                />
                <span className="truncate text-sm tracking-wide flex-1">{item.label}</span>
                
                {item.isRecadastramento ? (
                    <RecadastramentoStatusIndicator status={recadastramentoStatus} loading={isLoadingStatus} />
                ) : (
                    window.location.pathname === item.path && <span className="w-1.5 h-1.5 rounded-full bg-blue-500 ml-2"></span>
                )}
            </NavLink>
        );
    };

    return (
        <>
            {/* Overlay Mobile */}
            {isOpen && (
                <div 
                    className="fixed inset-0 bg-gray-900/60 backdrop-blur-[2px] z-[9990] lg:hidden transition-opacity duration-300" 
                    onClick={closeSidebar || toggleSidebar}
                ></div>
            )}

            <aside 
                className={`
                    fixed top-0 bottom-0 left-0 z-[9999]
                    flex flex-col w-[280px] flex-shrink-0 h-[100dvh]
                    bg-white dark:bg-gray-900 
                    border-r border-gray-200 dark:border-gray-800 
                    shadow-2xl lg:shadow-none
                    transform transition-transform duration-300 cubic-bezier(0.4, 0, 0.2, 1)
                    ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
                    lg:static lg:h-auto lg:z-auto lg:transform-none lg:transition-none
                `}
            >
                {/* 1. Header do Sidebar */}
                <div className="flex flex-col border-b border-gray-100 dark:border-gray-800/50 flex-shrink-0 relative bg-white dark:bg-gray-900 z-10">
                    
                    {/* Botão Fechar Mobile */}
                    <button 
                        onClick={closeSidebar || toggleSidebar} 
                        className="absolute right-3 top-3 lg:hidden text-gray-400 hover:text-gray-600 p-2"
                    >
                        <CloseIcon className="w-5 h-5" />
                    </button>

                    {/* Logo e Info do Servidor */}
                    <div className="flex flex-col items-center justify-center w-full px-6 pt-8 pb-4">
                        <div className="relative group mb-3">
                            <div className="absolute -inset-1 bg-gradient-to-r from-blue-600 to-cyan-600 rounded-full blur opacity-20 group-hover:opacity-40 transition duration-500"></div>
                            <div className="relative bg-white dark:bg-gray-800 rounded-full p-1 shadow-sm ring-1 ring-gray-100 dark:ring-gray-700">
                                {logoUrl && !imgError ? (
                                    <img
                                        src={logoUrl}
                                        alt="Logo"
                                        className="w-16 h-16 rounded-full object-contain"
                                        onError={() => setImgError(true)}
                                    />
                                ) : (
                                    <div className="w-16 h-16 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center text-blue-600 dark:text-blue-300">
                                        <User size={32} />
                                    </div>
                                )}
                            </div>
                        </div>
                        
                        <h2 className="text-sm font-bold text-gray-900 dark:text-white truncate max-w-[200px]">
                            {user?.name || 'Servidor'}
                        </h2>
                        
                        {vinculoSelecionado && (
                            <div className="mt-1 px-2 py-0.5 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded text-[10px] font-mono border border-blue-100 dark:border-blue-800">
                                Mat: {vinculoSelecionado.matricula}
                            </div>
                        )}
                    </div>

                    {/* Botão Trocar Vínculo */}
                    {vinculos && vinculos.length > 1 && (
                        <div className="px-4 pb-4">
                            <button
                                onClick={limparVinculoSelecionado}
                                className="w-full flex items-center justify-center gap-2 px-3 py-2 text-xs font-medium text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                            >
                                <Repeat size={14} className="text-blue-500" />
                                Trocar Vínculo
                            </button>
                        </div>
                    )}
                </div>

                {/* 2. Menu com Scroll */}
                <nav className="flex-1 py-4 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-200 dark:scrollbar-thumb-gray-800">
                    {menuStructure.map((group) => (
                        <div key={group.category} className="mb-2 last:mb-12">
                            {group.category !== 'Principal' && <MenuSectionTitle title={group.category} />}
                            <div className="space-y-0.5">
                                {group.items.map(item => (
                                    <MenuItem key={item.id} item={item} />
                                ))}
                            </div>
                        </div>
                    ))}
                </nav>

                {/* 3. Rodapé do Sidebar */}
                <div className="p-4 border-t border-gray-200 dark:border-gray-800 flex-shrink-0 bg-gray-50/50 dark:bg-black/20 pb-8 lg:pb-4 safe-bottom">
                    <div className="grid grid-cols-2 gap-2 mb-3">
                        {/* Botão Tema */}
                        <button 
                            onClick={toggleTheme} 
                            className="flex items-center justify-center px-3 py-2 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:border-amber-300 dark:hover:border-indigo-500 hover:shadow-sm transition-all text-xs font-medium text-gray-700 dark:text-gray-300 gap-2 group"
                        >
                            {theme === 'light' ? (
                                <>
                                    <Moon className="w-3.5 h-3.5 text-gray-400 group-hover:text-indigo-500 transition-colors" />
                                    <span>Escuro</span>
                                </>
                            ) : (
                                <>
                                    <Sun className="w-3.5 h-3.5 text-gray-400 group-hover:text-amber-500 transition-colors" />
                                    <span>Claro</span>
                                </>
                            )}
                        </button>

                        {/* Botão Sair */}
                        <button
                            onClick={handleLogout}
                            className="flex items-center justify-center px-3 py-2 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:border-red-300 dark:hover:border-red-700 hover:shadow-sm transition-all text-xs font-medium text-red-600 dark:text-red-400 gap-2 group"
                        >
                            <LogOut className="w-3.5 h-3.5 text-red-400 group-hover:text-red-600 transition-colors" />
                            <span>Sair</span>
                        </button>
                    </div>
                    
                    <div className="text-center">
                        <p className="text-[10px] text-gray-400 dark:text-gray-600">
                            Sisfol RH Online &copy; {new Date().getFullYear()}
                        </p>
                    </div>
                </div>
            </aside>
        </>
    );
};

export default Sidebar;